'use client'

import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  endOfDay, format,
  isBefore, startOfDay, subDays,
} from 'date-fns'
import { es } from 'date-fns/locale'
import { DateRange } from 'react-day-picker'
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ComposedChart,
} from 'recharts'
import {
  Calendar as CalendarIcon, Loader2, RefreshCw,
  TrendingUp, ShoppingBag, DollarSign, Users,
  Target, BarChart3, MapPin, Award, ShoppingCart,
} from 'lucide-react'

import { Header } from '@/components/layout/Header'
import { BottomNav } from '@/components/layout/BottomNav'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { useSalesReport } from '@/hooks/useSalesReport'
import { cn, formatCurrency } from '@/lib/utils'

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_RANGE: DateRange = {
  from: startOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
  to: endOfDay(new Date()),
}

const CHART_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
  '#f97316', '#a78bfa',
]

const ORIGIN_COLOR: Record<string, string> = {
  'Instagram': '#e1306c',
  'Caminando por el Mall': '#3b82f6',
  'Recomendación': '#10b981',
  'Cliente Recurrente': '#8b5cf6',
  'Redes Sociales (General)': '#f59e0b',
  'Venta Interna': '#6b7280',
  'Sin Especificar': '#94a3b8',
  'Otros': '#f97316',
}

const ORIGIN_CATEGORIES: Record<string, string[]> = {
  'Instagram': ['instagram', 'ig', 'insta', 'reel'],
  'Caminando por el Mall': ['caminando', 'pasando', 'paseando', 'mall', 'maquina', 'máquina'],
  'Recomendación': ['recomendación', 'recomendacion', 'por un amigo', 'referencia', 'amigo', 'familiar'],
  'Ya conocía la tienda': ['ya conocía', 'ya conocia', 'conocía la tienda', 'cliente frecuente'],
  'Redes Sociales (General)': ['redes sociales', 'facebook', 'fb', 'tiktok', 'twitter'],
  'Venta Interna': ['venta_interna', 'venta interna', 'empleado', 'staff'],
}

function categorizeOrigin(note: string | null | undefined, ordersCount?: number): string {
  const normalized = (note ?? '').trim().toLowerCase()
  const clienteKw = ['cliente', 'cliente antiguo', 'cliente recurrente', 'cliente frecuente']
  if (clienteKw.some(k => normalized.includes(k))) return 'Cliente Recurrente'
  if (ordersCount && ordersCount > 1) return 'Cliente Recurrente'
  if (!note || !note.trim()) return 'Sin Especificar'
  for (const [cat, kws] of Object.entries(ORIGIN_CATEGORIES)) {
    if (kws.some(k => normalized.includes(k.toLowerCase()))) return cat
  }
  return 'Otros'
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const CurrencyTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg bg-gray-900/95 border border-white/10 px-3 py-2 text-xs shadow-xl min-w-[140px]">
      {label && <p className="text-white/50 mb-1.5 font-medium">{label}</p>}
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">
          {p.name}: {typeof p.value === 'number' ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  )
}

const CountTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg bg-gray-900/95 border border-white/10 px-3 py-2 text-xs shadow-xl">
      {label && <p className="text-white/50 mb-1.5 font-medium">{label}</p>}
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color }} className="font-semibold">
          {p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('venta')
            ? formatCurrency(p.value)
            : p.value}
        </p>
      ))}
    </div>
  )
}

const PieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0]
  return (
    <div className="rounded-lg bg-gray-900/95 border border-white/10 px-3 py-2 text-xs shadow-xl">
      <p style={{ color: d.payload.fill }} className="font-semibold">{d.name}</p>
      <p className="text-white/70">{formatCurrency(d.value)}</p>
      <p className="text-white/50">{d.payload.pct}% del total</p>
    </div>
  )
}

// ─── Section header ───────────────────────────────────────────────────────────

function SectionTitle({ icon: Icon, title, subtitle }: { icon: any; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-2 mb-1">
      <Icon className="h-4 w-4 text-blue-400 flex-shrink-0" />
      <div>
        <h3 className="text-sm font-semibold text-white leading-tight">{title}</h3>
        {subtitle && <p className="text-xs text-white/40">{subtitle}</p>}
      </div>
    </div>
  )
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  icon: Icon, label, value, sub, color = 'blue', loading,
}: {
  icon: any; label: string; value: string; sub?: string
  color?: 'blue' | 'emerald' | 'amber' | 'purple' | 'white'
  loading?: boolean
}) {
  const colorMap = {
    blue: 'text-blue-400', emerald: 'text-emerald-400',
    amber: 'text-amber-400', purple: 'text-purple-400', white: 'text-white',
  }
  return (
    <Card className="border-white/10 bg-white/5 text-white backdrop-blur">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-white/50 mb-1 truncate">{label}</p>
            {loading
              ? <Skeleton className="h-7 w-28 bg-white/10" />
              : <p className={cn('text-xl font-bold tabular-nums truncate', colorMap[color])}>{value}</p>
            }
            {sub && !loading && <p className="text-xs text-white/40 mt-0.5">{sub}</p>}
          </div>
          <div className="p-2 rounded-lg bg-white/5 flex-shrink-0">
            <Icon className={cn('h-4 w-4', colorMap[color])} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ResumenPage() {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE)
  const [calOpen, setCalOpen] = useState(false)
  const { report, loading, getSalesReport } = useSalesReport()

  const handleGenerate = useCallback(async (range: DateRange = dateRange) => {
    if (!range.from || !range.to) return
    await getSalesReport(range.from, range.to, false)
  }, [dateRange, getSalesReport])

  useEffect(() => { handleGenerate(DEFAULT_RANGE) }, [])

  const dateLabel = useMemo(() => {
    const { from, to } = dateRange
    if (!from || !to) return 'Selecciona rango'
    if (format(from, 'yyyy-MM-dd') === format(to, 'yyyy-MM-dd'))
      return format(from, "d 'de' MMMM, yyyy", { locale: es })
    return `${format(from, 'dd/MM/yy')} – ${format(to, 'dd/MM/yy')}`
  }, [dateRange])

  // ── data derivations ──────────────────────────────────────────────────────

  const orders: any[] = useMemo(() => (report as any)?.orders ?? [], [report])
  const sellers: any[] = useMemo(() => (report as any)?.sellers ?? [], [report])
  const dailyPerf: any[] = useMemo(() => (report as any)?.dailyPerformance ?? [], [report])
  const budget: any = useMemo(() => (report as any)?.budgetSnapshot ?? null, [report])

  const kpis = useMemo(() => {
    const totalNetSales = (report as any)?.totalNetSales ?? 0
    const totalOrders = (report as any)?.totalOrders ?? 0
    const avgTicket = totalOrders > 0 ? totalNetSales / totalOrders : 0
    const best = [...sellers]
      .filter(s => s.sellerName !== 'Online' && s.sellerName !== 'Sin asignar' && s.totalNetSales > 0)
      .sort((a, b) => b.totalNetSales - a.totalNetSales)[0]
    const onlineSales = orders
      .filter(o => o.sellerName === 'Online')
      .reduce((s, o) => s + (o.netSales ?? 0), 0)
    const posPct = totalNetSales > 0 ? Math.round(((totalNetSales - onlineSales) / totalNetSales) * 100) : 0
    const budgetTotal = budget?.total ?? 0
    const progressPct = budgetTotal > 0 ? Math.round((totalNetSales / budgetTotal) * 100) : 0
    return { totalNetSales, totalOrders, avgTicket, best, onlineSales, posPct, progressPct, budgetTotal }
  }, [report, sellers, orders, budget])

  const dailyData = useMemo(() =>
    dailyPerf.map((d: any) => ({
      fecha: format(new Date(d.date), 'dd/MM', { locale: es }),
      'Venta Real': Math.round(d.totalActual ?? 0),
      'Estimado': Math.round(d.estimatedTotal ?? 0),
    })), [dailyPerf])

  const sellerData = useMemo(() =>
    [...sellers]
      .filter(s => s.sellerName !== 'Sin asignar' && s.sellerName !== 'Online' && s.totalNetSales > 0)
      .sort((a, b) => b.totalNetSales - a.totalNetSales)
      .slice(0, 8)
      .map(s => ({
        nombre: s.sellerName.length > 14 ? s.sellerName.slice(0, 13) + '…' : s.sellerName,
        fullName: s.sellerName,
        'Ventas': Math.round(s.totalNetSales),
        'Órdenes': s.totalOrders,
      })), [sellers])

  const channelData = useMemo(() => {
    const pos = orders.filter(o => o.sellerName !== 'Online' && o.sellerName !== 'Sin asignar')
      .reduce((s: number, o: any) => s + (o.netSales ?? 0), 0)
    const online = orders.filter(o => o.sellerName === 'Online')
      .reduce((s: number, o: any) => s + (o.netSales ?? 0), 0)
    const unassigned = orders.filter(o => o.sellerName === 'Sin asignar')
      .reduce((s: number, o: any) => s + (o.netSales ?? 0), 0)
    const total = pos + online + unassigned || 1
    const items = [
      { name: 'POS / Tienda', value: Math.round(pos), fill: '#3b82f6', pct: Math.round((pos / total) * 100) },
      { name: 'Online / Web', value: Math.round(online), fill: '#10b981', pct: Math.round((online / total) * 100) },
      { name: 'Sin asignar', value: Math.round(unassigned), fill: '#6b7280', pct: Math.round((unassigned / total) * 100) },
    ]
    return items.filter(i => i.value > 0)
  }, [orders])

  const productData = useMemo(() => {
    const map = new Map<string, { ventas: number; unidades: number }>()
    orders.forEach((o: any) => {
      ;(o.products ?? []).forEach((p: any) => {
        const name: string = p.name ?? 'Sin nombre'
        const cur = map.get(name) ?? { ventas: 0, unidades: 0 }
        map.set(name, {
          ventas: cur.ventas + (p.price ?? 0) * (p.quantity ?? 0),
          unidades: cur.unidades + (p.quantity ?? 0),
        })
      })
    })
    return Array.from(map.entries())
      .map(([name, d]) => ({
        nombre: name.length > 28 ? name.slice(0, 26) + '…' : name,
        'Ventas': Math.round(d.ventas),
        'Unidades': d.unidades,
      }))
      .sort((a, b) => b['Ventas'] - a['Ventas'])
      .slice(0, 10)
  }, [orders])

  const categoryData = useMemo(() => {
    const map = new Map<string, { ventas: number; unidades: number }>()
    orders.forEach((o: any) => {
      ;(o.products ?? []).forEach((p: any) => {
        const cat: string = p.product_type && p.product_type !== 'Otros' ? p.product_type : 'Otros'
        const cur = map.get(cat) ?? { ventas: 0, unidades: 0 }
        map.set(cat, {
          ventas: cur.ventas + (p.price ?? 0) * (p.quantity ?? 0),
          unidades: cur.unidades + (p.quantity ?? 0),
        })
      })
    })
    return Array.from(map.entries())
      .map(([name, d]) => ({
        nombre: name,
        'Ventas': Math.round(d.ventas),
        'Unidades': d.unidades,
      }))
      .sort((a, b) => b['Ventas'] - a['Ventas'])
      .slice(0, 8)
  }, [orders])

  const originData = useMemo(() => {
    const map = new Map<string, { count: number; ventas: number }>()
    orders.forEach((o: any) => {
      const cat = categorizeOrigin(o.note, o.customer?.orders_count)
      const cur = map.get(cat) ?? { count: 0, ventas: 0 }
      map.set(cat, { count: cur.count + 1, ventas: cur.ventas + (o.netSales ?? 0) })
    })
    const total = orders.length || 1
    return Array.from(map.entries())
      .map(([name, d]) => ({
        name,
        count: d.count,
        ventas: Math.round(d.ventas),
        pct: Math.round((d.count / total) * 100),
        fill: ORIGIN_COLOR[name] ?? '#94a3b8',
      }))
      .sort((a, b) => b.count - a.count)
  }, [orders])

  const goalsData = useMemo(() => {
    if (!budget?.total) return []
    return [...sellers]
      .filter(s => (s.goalPercentage ?? 0) > 0 && s.role !== 'ninguno')
      .map(s => {
        const meta = budget.total * ((s.goalPercentage ?? 0) / 100)
        const actual = s.totalNetSales ?? 0
        const pct = meta > 0 ? Math.round((actual / meta) * 100) : 0
        return {
          nombre: (s.sellerName ?? '').length > 14 ? (s.sellerName ?? '').slice(0, 13) + '…' : (s.sellerName ?? ''),
          fullName: s.sellerName,
          'Meta': Math.round(meta),
          'Actual': Math.round(actual),
          pct,
          color: pct >= 100 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444',
        }
      })
      .sort((a, b) => b['Actual'] - a['Actual'])
  }, [sellers, budget])

  const hasData = !!report && orders.length > 0
  const fmtK = (v: number) => `$${(v / 1000).toFixed(0)}k`

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Watermark */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.015]">
        <img src="/nibpr.svg" alt="" className="h-96 w-auto select-none" />
      </div>

      <div className="relative mx-auto w-full max-w-[1600px] px-4 py-8 pb-28 sm:px-6 lg:px-10">
        <Header />

        {/* ── Controls ─────────────────────────────────────────────────────── */}
        <Card className="mt-8 border-white/10 bg-white/5 backdrop-blur">
          <CardContent className="p-4 flex flex-wrap items-center gap-2">
            {/* Quick presets */}
            {[
              { label: 'Hoy', days: 1 },
              { label: '7 días', days: 7 },
              { label: '30 días', days: 30 },
            ].map(({ label, days }) => (
              <Button key={label} variant="outline" size="sm"
                className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20"
                onClick={() => {
                  const to = endOfDay(new Date())
                  const from = startOfDay(subDays(to, days - 1))
                  const r = { from, to }
                  setDateRange(r)
                  handleGenerate(r)
                }}>
                {label}
              </Button>
            ))}
            <Button variant="outline" size="sm"
              className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20"
              onClick={() => {
                const now = new Date()
                const from = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
                const to = endOfDay(now)
                const r = { from, to }
                setDateRange(r)
                handleGenerate(r)
              }}>
              Mes actual
            </Button>

            {/* Calendar picker */}
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="flex-shrink-0 bg-white/10 text-white hover:bg-white/20">
                  <CalendarIcon className="mr-2 h-4 w-4" />{dateLabel}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 bg-gray-900 border-white/20" align="start">
                <Calendar mode="range" numberOfMonths={2} selected={dateRange}
                  defaultMonth={dateRange.from}
                  onSelect={v => {
                    if (!v) return
                    setDateRange({ from: v.from ? startOfDay(v.from) : undefined, to: v.to ? endOfDay(v.to) : undefined })
                  }}
                  locale={es} disabled={(d) => isBefore(d, subDays(new Date(), 400))} />
              </PopoverContent>
            </Popover>

            <Button size="sm" onClick={() => handleGenerate()} disabled={loading}
              className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
              {loading
                ? <><Loader2 className="h-4 w-4 animate-spin" />Cargando...</>
                : <><RefreshCw className="h-4 w-4" />Generar</>}
            </Button>
          </CardContent>
        </Card>

        {/* ── KPI Strip ────────────────────────────────────────────────────── */}
        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
          <KpiCard icon={DollarSign} label="Venta Neta Total" color="emerald" loading={loading}
            value={formatCurrency(kpis.totalNetSales)}
            sub={kpis.budgetTotal > 0 ? `Meta: ${formatCurrency(kpis.budgetTotal)}` : undefined} />
          <KpiCard icon={ShoppingCart} label="Total Órdenes" color="blue" loading={loading}
            value={kpis.totalOrders.toLocaleString()} />
          <KpiCard icon={BarChart3} label="Ticket Promedio" color="amber" loading={loading}
            value={formatCurrency(kpis.avgTicket)} />
          <KpiCard icon={Award} label="Top Vendedor" color="purple" loading={loading}
            value={kpis.best?.sellerName ?? '—'}
            sub={kpis.best ? formatCurrency(kpis.best.totalNetSales) : undefined} />
          <KpiCard icon={Target} label="Cumplimiento Meta" loading={loading}
            color={kpis.progressPct >= 100 ? 'emerald' : kpis.progressPct >= 70 ? 'amber' : 'white'}
            value={kpis.budgetTotal > 0 ? `${kpis.progressPct}%` : '—'}
            sub={kpis.budgetTotal > 0 ? `POS ${kpis.posPct}% | Online ${100 - kpis.posPct}%` : undefined} />
        </div>

        {/* ── Loading placeholder ───────────────────────────────────────────── */}
        {loading && (
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-72 w-full rounded-xl bg-white/5" />
            ))}
          </div>
        )}

        {/* ── No data ───────────────────────────────────────────────────────── */}
        {!loading && !hasData && (
          <div className="mt-12 text-center text-white/40">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg">Sin datos para el rango seleccionado</p>
            <p className="text-sm mt-1">Selecciona un rango y presiona Generar</p>
          </div>
        )}

        {/* ── Charts grid ───────────────────────────────────────────────────── */}
        {!loading && hasData && (
          <div className="mt-6 space-y-4">

            {/* Row 1: Daily Trend (full width) */}
            <Card className="border-white/10 bg-white/5 backdrop-blur">
              <CardHeader className="pb-2 pt-4 px-5">
                <SectionTitle icon={TrendingUp} title="Tendencia Diaria de Ventas"
                  subtitle="Venta real vs estimado por día del período" />
              </CardHeader>
              <CardContent className="px-2 pb-4">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={dailyData} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gReal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gEst" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ffffff" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#ffffff" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="fecha" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtK} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} width={42} />
                    <Tooltip content={<CurrencyTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} />
                    <Area type="monotone" dataKey="Estimado" stroke="rgba(255,255,255,0.25)"
                      strokeDasharray="4 4" fill="url(#gEst)" strokeWidth={1.5} dot={false} />
                    <Area type="monotone" dataKey="Venta Real" stroke="#3b82f6"
                      fill="url(#gReal)" strokeWidth={2.5} dot={false}
                      activeDot={{ r: 5, fill: '#3b82f6', stroke: '#1e3a5f' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Row 2: Sellers (2/3) + Channel Pie (1/3) */}
            <div className="grid gap-4 lg:grid-cols-3">
              <Card className="lg:col-span-2 border-white/10 bg-white/5 backdrop-blur">
                <CardHeader className="pb-2 pt-4 px-5">
                  <SectionTitle icon={Users} title="Rendimiento por Vendedor"
                    subtitle="Venta neta acumulada por vendedor" />
                </CardHeader>
                <CardContent className="px-2 pb-4">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={sellerData} layout="vertical"
                      margin={{ top: 0, right: 40, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                      <XAxis type="number" tickFormatter={fmtK}
                        tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="nombre" width={90}
                        tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CurrencyTooltip />} />
                      <Bar dataKey="Ventas" radius={[0, 4, 4, 0]} maxBarSize={22}>
                        {sellerData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border-white/10 bg-white/5 backdrop-blur">
                <CardHeader className="pb-2 pt-4 px-5">
                  <SectionTitle icon={ShoppingBag} title="Canal de Ventas"
                    subtitle="Distribución POS vs Online" />
                </CardHeader>
                <CardContent className="px-2 pb-4 flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={channelData} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3}>
                        {channelData.map((d, i) => (
                          <Cell key={i} fill={d.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="w-full space-y-1.5 mt-1">
                    {channelData.map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full" style={{ background: d.fill }} />
                          <span className="text-white/70">{d.name}</span>
                        </div>
                        <span className="font-semibold tabular-nums" style={{ color: d.fill }}>
                          {d.pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Row 3: Goals + Categories */}
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Goals progress */}
              <Card className="border-white/10 bg-white/5 backdrop-blur">
                <CardHeader className="pb-2 pt-4 px-5">
                  <SectionTitle icon={Target} title="Progreso de Metas"
                    subtitle="Venta actual vs meta mensual por vendedor" />
                </CardHeader>
                <CardContent className="px-2 pb-4">
                  {goalsData.length === 0 ? (
                    <p className="text-center text-white/30 text-sm py-10">Sin metas configuradas para este período</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(goalsData.length * 50 + 20, 240)}>
                      <ComposedChart data={goalsData} layout="vertical"
                        margin={{ top: 0, right: 50, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                        <XAxis type="number" tickFormatter={fmtK}
                          tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="nombre" width={90}
                          tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CurrencyTooltip />} />
                        <Bar dataKey="Meta" fill="rgba(255,255,255,0.1)" radius={[0, 4, 4, 0]} maxBarSize={20} />
                        <Bar dataKey="Actual" radius={[0, 4, 4, 0]} maxBarSize={20}>
                          {goalsData.map((d, i) => (
                            <Cell key={i} fill={d.color} />
                          ))}
                        </Bar>
                      </ComposedChart>
                    </ResponsiveContainer>
                  )}
                  {goalsData.length > 0 && (
                    <div className="flex gap-4 justify-center mt-2 text-xs">
                      <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded bg-white/15 inline-block" />Meta</span>
                      <span className="flex items-center gap-1.5 text-emerald-400"><span className="h-2 w-4 rounded bg-emerald-500 inline-block" />≥100%</span>
                      <span className="flex items-center gap-1.5 text-amber-400"><span className="h-2 w-4 rounded bg-amber-500 inline-block" />70-99%</span>
                      <span className="flex items-center gap-1.5 text-red-400"><span className="h-2 w-4 rounded bg-red-500 inline-block" />&lt;70%</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Categories */}
              <Card className="border-white/10 bg-white/5 backdrop-blur">
                <CardHeader className="pb-2 pt-4 px-5">
                  <SectionTitle icon={BarChart3} title="Ventas por Categoría"
                    subtitle="Tipo de producto más vendido" />
                </CardHeader>
                <CardContent className="px-2 pb-4">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={categoryData} layout="vertical"
                      margin={{ top: 0, right: 40, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                      <XAxis type="number" tickFormatter={fmtK}
                        tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="nombre" width={80}
                        tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CurrencyTooltip />} />
                      <Bar dataKey="Ventas" radius={[0, 4, 4, 0]} maxBarSize={22}>
                        {categoryData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[(i + 3) % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Row 4: Top Products + Origin */}
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Top products */}
              <Card className="border-white/10 bg-white/5 backdrop-blur">
                <CardHeader className="pb-2 pt-4 px-5">
                  <SectionTitle icon={ShoppingBag} title="Top 10 Productos"
                    subtitle="Por venta neta en el período" />
                </CardHeader>
                <CardContent className="px-2 pb-4">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={productData} layout="vertical"
                      margin={{ top: 0, right: 50, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" />
                      <XAxis type="number" tickFormatter={fmtK}
                        tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="nombre" width={120}
                        tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CountTooltip />} />
                      <Bar dataKey="Ventas" radius={[0, 4, 4, 0]} maxBarSize={20}>
                        {productData.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.85} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Origin */}
              <Card className="border-white/10 bg-white/5 backdrop-blur">
                <CardHeader className="pb-2 pt-4 px-5">
                  <SectionTitle icon={MapPin} title="Origen de Ventas"
                    subtitle="¿Cómo nos conocieron los clientes?" />
                </CardHeader>
                <CardContent className="px-3 pb-4 flex flex-col gap-3">
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={originData} dataKey="count" nameKey="name"
                        cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {originData.map((d, i) => (
                          <Cell key={i} fill={d.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: any, name: string) => [`${v} órdenes`, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5">
                    {originData.map((d, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: d.fill }} />
                        <span className="text-white/70 flex-1 truncate">{d.name}</span>
                        <span className="tabular-nums text-white/50">{d.count}</span>
                        <div className="w-16 h-1.5 rounded-full bg-white/10 flex-shrink-0">
                          <div className="h-full rounded-full" style={{ width: `${d.pct}%`, background: d.fill }} />
                        </div>
                        <span className="tabular-nums font-semibold w-7 text-right" style={{ color: d.fill }}>
                          {d.pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Row 5: Seller detail table */}
            <Card className="border-white/10 bg-white/5 backdrop-blur">
              <CardHeader className="pb-2 pt-4 px-5">
                <SectionTitle icon={Users} title="Detalle por Vendedor"
                  subtitle="Resumen completo de métricas individuales" />
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 border-b border-white/10">
                    <tr>
                      {['Vendedor', 'Venta Neta', 'Órdenes', 'Ticket Prom.', 'Rol', '% Meta', 'Progreso'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left text-xs text-white/40 font-medium first:pl-5 last:pr-5">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {[...sellers]
                      .filter(s => s.totalNetSales > 0)
                      .sort((a: any, b: any) => b.totalNetSales - a.totalNetSales)
                      .map((s: any, i) => {
                        const goalPct = s.goalPercentage ?? 0
                        const meta = budget?.total ? budget.total * goalPct / 100 : 0
                        const progress = meta > 0 ? Math.min((s.totalNetSales / meta) * 100, 150) : 0
                        const progColor = progress >= 100 ? '#10b981' : progress >= 70 ? '#f59e0b' : '#ef4444'
                        return (
                          <tr key={i} className="hover:bg-white/5 transition-colors">
                            <td className="px-4 py-2.5 pl-5 font-medium">{s.sellerName}</td>
                            <td className="px-4 py-2.5 tabular-nums font-semibold text-emerald-400">
                              {formatCurrency(s.totalNetSales)}
                            </td>
                            <td className="px-4 py-2.5 tabular-nums text-white/70">{s.totalOrders}</td>
                            <td className="px-4 py-2.5 tabular-nums text-white/70">{formatCurrency(s.avgTicket)}</td>
                            <td className="px-4 py-2.5">
                              {s.role && s.role !== 'ninguno' ? (
                                <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium border',
                                  s.role === 'principal'
                                    ? 'bg-blue-500/20 text-blue-300 border-blue-500/30'
                                    : 'bg-purple-500/20 text-purple-300 border-purple-500/30')}>
                                  {s.role === 'principal' ? 'Principal' : 'Secundario'}
                                </span>
                              ) : <span className="text-white/25 text-xs">—</span>}
                            </td>
                            <td className="px-4 py-2.5 tabular-nums text-white/60">
                              {goalPct > 0 ? `${goalPct.toFixed(1)}%` : <span className="text-white/25">—</span>}
                            </td>
                            <td className="px-4 py-2.5 pr-5">
                              {meta > 0 ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-20 h-1.5 rounded-full bg-white/10">
                                    <div className="h-full rounded-full transition-all"
                                      style={{ width: `${Math.min(progress, 100)}%`, background: progColor }} />
                                  </div>
                                  <span className="text-xs tabular-nums font-semibold" style={{ color: progColor }}>
                                    {Math.round(progress)}%
                                  </span>
                                </div>
                              ) : <span className="text-white/25 text-xs">—</span>}
                            </td>
                          </tr>
                        )
                      })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  )
}
